import React, { useState } from "react";
import { motion } from "framer-motion";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, LoadingState, StatusChip } from "@/components/app/primitives";

export default function FlowPage() {
  const { data: steps, loading } = useFetch("/flow");
  const [selected, setSelected] = useState(0);

  if (loading) return <LoadingState />;
  const step = steps?.[selected];

  return (
    <div data-testid="flow-page">
      <PageHeader
        kicker="Scene 3C - Operating model"
        title="From Consultant Judgment to Agentic Execution"
        subtitle="How senior judgment becomes repeatable execution. Click any step to see what SIX does, what Brightspeed provides, what AI supports, and what output is produced."
      />

      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="flow-step-rail">
        {(steps || []).map((s, i) => (
          <button
            key={s.id}
            onClick={() => setSelected(i)}
            data-testid={`flow-step-button-${s.step}`}
            className={`group flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors active:scale-[0.98] ${i === selected ? "border-[--six-crimson] bg-[--six-crimson-soft]" : "border-[--hairline] bg-[--panel-dark] hover:bg-[--shell-3]"}`}
          >
            <span className={`font-display text-sm font-bold tabular-nums ${i === selected ? "text-[#E86A6A]" : "text-[--tx-3]"}`}>{String(s.step).padStart(2, "0")}</span>
            <span className={`text-xs font-medium ${i === selected ? "text-[--tx-1]" : "text-[--tx-2]"}`}>{s.title}</span>
          </button>
        ))}
      </div>

      {step && (
        <motion.div key={step.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.16 }}>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="flow-step-detail">
            {[
              { label: "What SIX does", value: step.six_does, tone: "crimson" },
              { label: "What Brightspeed provides", value: step.brightspeed_provides, tone: "neutral" },
              { label: "What AI supports", value: step.ai_supports, tone: "ai" },
              { label: "Output produced", value: step.output, tone: "validated" },
            ].map((c) => (
              <Panel key={c.label} title={c.label} className="h-full">
                <StatusChip tone={c.tone} className="mb-2">{step.title}</StatusChip>
                <p className="text-[13px] text-[--tx-2] leading-relaxed">{c.value}</p>
              </Panel>
            ))}
          </div>
        </motion.div>
      )}

      <div className="mt-5 rounded-xl border border-[--divider] bg-[--shell-2] p-4">
        <p className="text-xs text-[--tx-3] leading-relaxed">
          The flow is a loop, not a line: benefit tracking (12) feeds new senior hypotheses (1). Every cycle raises data confidence, sharpens targeting, and transfers capability to Brightspeed owners. SIX's role shrinks by design as the operating system takes hold.
        </p>
      </div>
    </div>
  );
}
